import { FAQ_ITEMS } from '@/components/organisms/FAQSection/faqItems';

export const SITE_ORIGIN = 'https://runassay.app';
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/images/dark/og.png`;

export type JsonLd = Record<string, unknown>;

export type RouteMeta = {
  canonical: string;
  description: string;
  jsonLd?: JsonLd[];
  ogImage?: string;
  robots?: string;
  title: string;
};

const DEFAULT_DESCRIPTION =
  'Assay is a personal CRM for staying in touch. It reminds you who to reconnect with, remembers every conversation, and drafts the message - so the people who matter never slip through the cracks.';

const buildFaqJsonLd = (): JsonLd => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answerPlain,
    },
  })),
});

const softwareApplicationJsonLd: JsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Assay',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Personal CRM',
  operatingSystem: 'Web',
  url: `${SITE_ORIGIN}/`,
  description: DEFAULT_DESCRIPTION,
  keywords:
    'personal CRM, stay in touch app, relationship manager, network CRM, keep in touch reminders',
  offers: {
    '@type': 'AggregateOffer',
    lowPrice: '0',
    priceCurrency: 'USD',
    offerCount: '4',
  },
  publisher: {
    '@type': 'Organization',
    name: 'Assay',
    url: `${SITE_ORIGIN}/`,
  },
};

const pricingProductJsonLd: JsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Assay',
  description:
    'A personal CRM for staying in touch: cadence reminders, an interaction log, and AI-drafted check-ins. Free on Trace, with paid Seam, Vein, and Nugget plans.',
  brand: { '@type': 'Brand', name: 'Assay' },
  offers: {
    '@type': 'AggregateOffer',
    lowPrice: '0',
    priceCurrency: 'USD',
    offerCount: '4',
  },
};

const ROUTE_META: Record<string, RouteMeta> = {
  '/': {
    title: 'Assay - The personal CRM that keeps your relationships warm',
    description:
      'Assay reminds you who to reconnect with, remembers every conversation, and drafts the message. A personal CRM for staying in touch with the people who matter.',
    canonical: '/',
    jsonLd: [softwareApplicationJsonLd],
  },
  '/pricing': {
    title: 'Pricing - Assay',
    description:
      'Start free on Trace with 25 contacts and stay-in-touch reminders. Upgrade to Seam for unlimited contacts, Gmail import, and the full personal-CRM workflow.',
    canonical: '/pricing',
    jsonLd: [pricingProductJsonLd],
  },
  '/about': {
    title: 'About Assay - A personal CRM for staying in touch',
    description:
      "Why Assay exists: your network goes quiet without you noticing. Sort the people you know, see who's due for a catch-up, and send a message that sounds like you. Founder-built and opinionated.",
    canonical: '/about',
  },
  '/faq': {
    title: 'Assay FAQ - Personal CRM, reminders, AI drafts, privacy',
    description:
      "Straight answers on what Assay does (and doesn't), how the stay-in-touch reminders and AI drafts work, where your data lives, the free Trace tier, and importing your network.",
    canonical: '/faq',
    jsonLd: [buildFaqJsonLd()],
  },
  '/privacy': {
    title: 'Privacy Policy - Assay',
    description:
      'How Assay collects, processes, stores, and protects your data, including Anthropic AI processing terms and your rights as a data subject.',
    canonical: '/privacy',
  },
  '/terms': {
    title: 'Terms of Service - Assay',
    description:
      'Terms governing your use of Assay, including subscription, content, and account responsibilities.',
    canonical: '/terms',
  },
  '/dpa': {
    title: 'Data Processing Addendum - Assay',
    description:
      "Assay's Data Processing Addendum covering processor obligations, sub-processors, and security measures.",
    canonical: '/dpa',
  },
  '/signin': {
    title: 'Sign in - Assay',
    description: 'Sign in to Assay.',
    canonical: '/signin',
    robots: 'noindex, follow',
  },
  '/signup': {
    title: 'Sign up - Assay',
    description: 'Create an Assay account.',
    canonical: '/signup',
    robots: 'noindex, follow',
  },
};

const APP_FALLBACK: RouteMeta = {
  title: 'Assay',
  description: DEFAULT_DESCRIPTION,
  canonical: '/',
  robots: 'noindex, follow',
};

const NOT_FOUND_META: RouteMeta = {
  title: 'Not found - Assay',
  description: 'The page you were looking for could not be found.',
  canonical: '/',
  robots: 'noindex, follow',
};

const PUBLIC_PATHS = new Set(Object.keys(ROUTE_META));

export function getRouteMeta(pathname: string): RouteMeta {
  if (ROUTE_META[pathname]) return ROUTE_META[pathname];

  if (pathname.startsWith('/app') || pathname.startsWith('/onboarding')) {
    return APP_FALLBACK;
  }

  if (PUBLIC_PATHS.has(pathname)) return ROUTE_META[pathname];

  return NOT_FOUND_META;
}
